import React from "react"
import { Message } from "@/reducers/messagesSlice"
import { isMobile } from "@/utils/detectDevice"
import { useAppSelector } from "@/reducers/hooks"
import {
  selectActiveChat,
  selectDocumentDriverContext,
  selectDocumentMetadata,
} from "@/reducers/chatsSliceSelectors"
import { selectChatMessages } from "@/reducers/messagesSliceSelectors"
import { selectImageGallery, selectPDFViewer } from "@/reducers/overlaysSlice"
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerPortal,
  DrawerTitle,
} from "@/components/ui/drawer"
import CMRMetaData from "./cmr-meta-data"
import { Button } from "@/components/ui/button"
import { MoreVertical } from "lucide-react"

interface CMRMetaDataDrawerProps {
  message: Message
}

export const CMRMetaDataDrawer: React.FC<CMRMetaDataDrawerProps> = ({
  message,
}) => {
  const activeChat = useAppSelector(selectActiveChat)
  const chatMessages = useAppSelector(selectChatMessages(activeChat.jid))

  // Select the appropriate viewer state based on viewerType
  const pdfViewer = useAppSelector(selectPDFViewer)
  const imageGallery = useAppSelector(selectImageGallery)
  const drawerOpen = pdfViewer.visible
    ? pdfViewer.drawerOpen
    : imageGallery.drawerOpen

  const [isDrawerOpen, setIsDrawerOpen] = React.useState<boolean>(!!drawerOpen)

  const documentMetadata = selectDocumentMetadata(chatMessages, message?.id)
  const driverContext = selectDocumentDriverContext(chatMessages, message?.id)
  const hasCMRMetadata = Boolean(documentMetadata?.data)

  if (!message || !isMobile || !hasCMRMetadata) {
    return null
  }

  return (
    <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
      <Button onClick={() => setIsDrawerOpen(true)} size="icon">
        <MoreVertical />
      </Button>
      <DrawerPortal>
        <DrawerHeader>
          <DrawerTitle />
        </DrawerHeader>

        <DrawerContent className="z-[99999]">
          <div
            onWheel={(e) => isDrawerOpen && e.stopPropagation()}
            onTouchMove={(e) => isDrawerOpen && e.stopPropagation()}
            className="flex-1 overflow-y-auto rounded-t-[6px] bg-white p-4 dark:bg-black"
            style={{ maxHeight: "80dvh" }}
          >
            <CMRMetaData
              showAllData
              message={message}
              metadata={documentMetadata?.data}
              driverContext={driverContext}
              documentType={documentMetadata?.documentType}
              blurry={documentMetadata?.blurry}
              remarks={documentMetadata?.remarks}
            />
          </div>
        </DrawerContent>
      </DrawerPortal>
    </Drawer>
  )
}

export default CMRMetaDataDrawer
